/** The memory globe (spec §7.1): the canvas, its camera, the arc tooltip and a keyboard list of what it draws. The scene is rebuilt
 * when the globe or the focus view changes; frames redraw from refs, without React renders. */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { YOU } from "@/api/memory-focus";
import { sharpZoomOf } from "@/components/canvas/webkit-sharp-zoom";
import { Button, PocketPanel, Spinner } from "@/components/ui";
import { useT, type Translator } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import type en from "@/i18n/messages/frames-memory/en";
import { kindWord } from "@/lib/memory-thing-kinds";
import { trayProfileInitials } from "@/desktop/tray-profile-initials";
import { THEME_CHANGED_EVENT } from "@/lib/theme";
import { prefersReducedMotion } from "@/lib/reveal-motion";
import type { PocketEntry } from "@/stores/memory-pocket-stack";
import { useMemoryStore } from "@/stores/memory-store";
import {
  backingRatio,
  canvasPoint,
  drawGlobe,
  globeFactAt,
  globeNodeAt,
  graphArea,
  initialFades,
  projectScene,
  readPalette,
  sizeCanvas,
  stepFades,
  type Point,
  type ProjectedScene,
} from "./globe-renderer";
import type { Angles } from "./globe-projection";
import { buildGlobeScene, globeLayout, type SceneNode } from "./globe-scene";
import { useFrameChromeHidden } from "../shared/frame-renderer-context";
import { useGlobeCamera, type GlobePointer } from "./use-globe-camera";
import { useMemoryFrameView } from "./use-memory-frame-view";
const NO_LIT_IDS: readonly string[] = [];
const LIST_BUTTON =
  "sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:left-4 focus-visible:top-4 focus-visible:z-[5]";
/** The arc the pocket shows: the open fact (also under Sources or History), or the suggestion under review. */
function openArcKey(stack: readonly PocketEntry[]): string | null {
  const top = stack[stack.length - 1];
  if (
    top?.level === "fact" ||
    top?.level === "sources" ||
    top?.level === "history"
  )
    return top.id;
  return top?.level === "suggestion" ? `s:${top.id}` : null;
}
function thingLabel(
  node: SceneNode,
  focusKey: string | null,
  t: Translator<typeof en>,
): string {
  if (node.key === YOU) return t("you");
  return [
    t("globeThing", {
      name: node.name,
      kind: kindWord(node.kind, 1),
    }),
    node.more > 0 ? t("globeThingMore", { count: node.more }) : "",
    node.pending ? t("globeThingSuggested") : "",
    node.key === focusKey ? t("globeThingInFocus") : "",
  ].join("");
}
/** A thing on the globe or in its list: a suggested new thing opens its review; anything else becomes the focus. */
function activate(node: SceneNode): void {
  const store = useMemoryStore.getState();
  if (node.thingId !== null) store.select({ type: "thing", id: node.key });
  else if (node.suggestionIds[0]) store.openReview(node.suggestionIds[0]);
}
export function MemoryGlobe({
  frameId,
  pixelBoost,
  pocketInside,
  youName,
  youEmail,
}: {
  frameId: string;
  pixelBoost: number;
  pocketInside: boolean;
  youName: string;
  youEmail?: string | null;
}) {
  const t = useT(framesMemoryMessages);
  const common = useT(commonMessages);
  const globe = useMemoryStore((s) => s.globe);
  const globeError = useMemoryStore((s) => s.globeError);
  const focusId = useMemoryStore((s) => s.focusId);
  const focusView = useMemoryStore((s) =>
    s.focusViewId === s.focusId ? s.focusView : null,
  );
  const fitCount = useMemoryStore((s) => s.fitCount);
  const aimCount = useMemoryStore((s) => s.aimCount);
  const openArc = useMemoryStore((s) => openArcKey(s.pocketStack));
  const [recencyNow, setRecencyNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setRecencyNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const youGlyph = trayProfileInitials(youName, youEmail);
  const layout = useMemo(() => (globe ? globeLayout(globe) : null), [globe]);
  const scene = useMemo(
    () =>
      globe && layout
        ? buildGlobeScene({
            globe,
            layout,
            focus: focusView,
            focusId,
            youGlyph,
            now: recencyNow,
          })
        : null,
    [globe, layout, focusView, focusId, youGlyph, recencyNow],
  );
  useMemoryFrameView(frameId, scene?.litIds ?? NO_LIT_IDS);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [tip, setTip] = useState<{ text: string; x: number; y: number } | null>(
    null,
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const hover = useRef<{ node: string | null; arc: string | null }>({
    node: null,
    arc: null,
  });
  const fades = useRef(initialFades());
  const projected = useRef<ProjectedScene | null>(null);
  const draw = useRef<(now: number) => void>(() => undefined);
  const frame = useRef({ raf: 0, fallback: 0, last: 0 });
  const aimed = useRef<{
    key: string;
    fit: number;
    aim: number;
    target: Angles;
  } | null>(null);
  // Clearing the ids matters: a stale id makes requestDraw think a frame is
  // still pending, so a remount (StrictMode, HMR) would never redraw again.
  const cancelDraw = useCallback(() => {
    const pending = frame.current;
    cancelAnimationFrame(pending.raf);
    window.clearTimeout(pending.fallback);
    pending.raf = 0;
    pending.fallback = 0;
  }, []);
  const flushDraw = useCallback(
    (now: number) => {
      cancelDraw();
      draw.current(now);
    },
    [cancelDraw],
  );
  const requestDraw = useCallback(() => {
    const pending = frame.current;
    if (pending.raf || pending.fallback) return;
    pending.raf = requestAnimationFrame(flushDraw);
    // Transparent Tauri webviews can pause animation frames while pointer
    // events still arrive. Keep direct globe manipulation responsive.
    pending.fallback = window.setTimeout(
      () => flushDraw(performance.now()),
      48,
    );
  }, [flushDraw]);
  const palette = useRef<ReturnType<typeof readPalette> | null>(null);
  useLayoutEffect(() => {
    const read = () => {
      if (canvasRef.current) palette.current = readPalette(canvasRef.current);
      requestDraw();
    };
    read();
    window.addEventListener(THEME_CHANGED_EVENT, read);
    return () => window.removeEventListener(THEME_CHANGED_EVENT, read);
  }, [requestDraw]);
  const toLocal = useCallback((pointer: GlobePointer): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: pointer.clientX, y: pointer.clientY };
    return canvasPoint(
      pointer,
      canvas.getBoundingClientRect(),
      canvas.offsetWidth,
      sharpZoomOf(canvas),
    );
  }, []);
  const onHover = useCallback(
    (point: Point | null) => {
      const node =
        point && projected.current
          ? globeNodeAt(projected.current, point)
          : null;
      const arc =
        point && projected.current && !node
          ? globeFactAt(projected.current, point)
          : null;
      const next = { node: node?.key ?? null, arc: arc?.key ?? null };
      if (next.node !== hover.current.node || next.arc !== hover.current.arc) {
        hover.current = next;
        requestDraw();
      }
      if (canvasRef.current)
        canvasRef.current.style.cursor = node || arc ? "pointer" : "grab";
      setTip(arc && point ? { text: arc.text, x: point.x, y: point.y } : null);
    },
    [requestDraw],
  );
  const onTap = useCallback((point: Point) => {
    const node = projected.current
      ? globeNodeAt(projected.current, point)
      : null;
    const arc =
      projected.current && !node ? globeFactAt(projected.current, point) : null;
    const store = useMemoryStore.getState();
    if (node) activate(node);
    else if (arc?.suggestionId) store.openReview(arc.suggestionId);
    else if (arc?.factId) store.select({ type: "fact", id: arc.factId });
    else store.closePocket();
  }, []);
  const camera = useGlobeCamera({ toLocal, requestDraw, onTap, onHover });
  const onTile = useFrameChromeHidden();
  useLayoutEffect(() => {
    draw.current = (now) => {
      const canvas = canvasRef.current;
      if (!canvas || !scene || size.width === 0) return;
      const dt = frame.current.last
        ? Math.min(48, now - frame.current.last)
        : 16;
      frame.current.last = now;
      const moving = camera.step(now);
      const fading = stepFades(
        fades.current,
        scene,
        dt,
        prefersReducedMotion(),
      );
      const view = projectScene(scene, camera.camera.current, fades.current);
      const ctx = canvas.getContext("2d");
      projected.current = view;
      if (ctx && palette.current)
        drawGlobe(ctx, view, {
          palette: palette.current,
          width: size.width,
          height: size.height,
          hoverKey: hover.current.node,
          hotArcKeys: new Set(
            [openArc, hover.current.arc].filter(
              (key): key is string => key !== null,
            ),
          ),
          pocket: pocketInside
            ? { x0: size.width - 304, x1: size.width, y0: 0, y1: size.height }
            : null,
          hoverLabelOnly: onTile,
        });
      if (moving || fading) requestDraw();
      else frame.current.last = 0;
    };
  });
  useLayoutEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      if (entry)
        setSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
    });
    if (rootRef.current) observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, []);
  const ratio = backingRatio(window.devicePixelRatio || 1, pixelBoost);
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || size.width === 0) return;
    sizeCanvas(canvas, canvas.getContext("2d"), size.width, size.height, ratio);
    camera.setArea(graphArea(size.width, size.height, pocketInside));
    draw.current(performance.now());
  }, [camera, pocketInside, ratio, size]);
  useLayoutEffect(() => {
    if (!scene) return;
    const [key, previous] = [scene.focusKey ?? YOU, aimed.current];
    // An open waits until the scene has placed its thing; a thing that is
    // already the focus is followed when a reload moves it.
    const placed = key === focusId;
    if (!previous) camera.jump(scene.target, 1);
    else if (previous.fit !== fitCount) camera.turnTo(scene.target, 1);
    else if (
      previous.key !== key ||
      (placed && previous.aim !== aimCount) ||
      (key !== YOU &&
        (previous.target.yaw !== scene.target.yaw ||
          previous.target.pitch !== scene.target.pitch))
    )
      camera.turnTo(scene.target);
    aimed.current = {
      key,
      fit: fitCount,
      aim: placed || !previous ? aimCount : previous.aim,
      target: scene.target,
    };
    draw.current(performance.now());
  }, [aimCount, camera, fitCount, focusId, scene]);
  useEffect(() => {
    const canvas = canvasRef.current;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      camera.zoomBy(event.deltaY);
    };
    // On the wall's Pinned tile the wheel scrolls the wall, never the globe.
    if (!onTile) canvas?.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      canvas?.removeEventListener("wheel", onWheel);
      cancelDraw();
    };
  }, [camera, cancelDraw, onTile]);
  useLayoutEffect(() => {
    const element = tipRef.current;
    if (!tip || !element) return;
    const x =
      tip.x + 14 + element.offsetWidth > size.width - 8
        ? tip.x - element.offsetWidth - 14
        : tip.x + 14;
    const y =
      tip.y + 16 + element.offsetHeight > size.height - 8
        ? tip.y - element.offsetHeight - 12
        : tip.y + 16;
    element.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  }, [size, tip]);
  const listed = scene
    ? scene.nodes
        .filter((node) => node.lit)
        .sort((a, b) =>
          a.key === scene.focusKey
            ? -1
            : b.key === scene.focusKey
              ? 1
              : a.rank - b.rank,
        )
    : [];
  const setHover = (key: string | null) => {
    hover.current = { node: key, arc: null };
    requestDraw();
  };
  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      className="absolute inset-0 outline-none"
      data-testid="memory-globe"
    >
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        data-testid="memory-globe-canvas"
        className="nodrag nopan nowheel absolute inset-0 h-full w-full cursor-grab touch-none"
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          rootRef.current?.focus({ preventScroll: true });
          camera.handlers.onPointerDown(event);
        }}
        onPointerMove={(event) => {
          if (event.buttons) event.stopPropagation();
          camera.handlers.onPointerMove(event);
        }}
        onPointerUp={(event) => {
          event.stopPropagation();
          camera.handlers.onPointerUp(event);
        }}
        onPointerCancel={(event) => {
          event.stopPropagation();
          camera.handlers.onPointerCancel(event);
        }}
        onPointerLeave={camera.handlers.onPointerLeave}
      />
      {!globe && !globeError ? (
        <div className="absolute inset-0 grid place-items-center">
          <Spinner className="h-5 w-5 text-muted" />
        </div>
      ) : null}
      {!globe && globeError ? (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3"
          data-testid="memory-globe-error"
        >
          <p className="text-appkit-body text-muted">{t("globeLoadFailed")}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void useMemoryStore.getState().loadGlobe()}
            data-testid="memory-globe-retry"
          >
            {common("retry")}
          </Button>
        </div>
      ) : null}
      {tip ? (
        <PocketPanel
          ref={tipRef}
          nativeGroup="memory-arc-tooltip"
          nativeSurface={false}
          testId="memory-arc-tooltip"
          role="tooltip"
          className="pointer-events-none absolute left-0 top-0 z-[7] max-w-60 px-2.5 py-1.5 text-appkit-caption [text-wrap:pretty]"
        >
          {tip.text}
        </PocketPanel>
      ) : null}
      <ul
        aria-label={t("thingsOnGlobe")}
        className="m-0 list-none p-0"
        data-testid="memory-globe-list"
      >
        {listed.map((node) => (
          <li key={node.key}>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className={LIST_BUTTON}
              aria-label={thingLabel(node, scene?.focusKey ?? null, t)}
              data-testid={`memory-globe-thing-${node.key}`}
              onFocus={() => setHover(node.key)}
              onBlur={() => setHover(null)}
              onClick={() => activate(node)}
            >
              {node.name}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
